import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Phone, ArrowRight } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import { SERVICES, PHONE_TEL } from "@/constants/site";

export default function Services() {
  return (
    <section id="services" className="bg-[#FAFAFA] py-20 sm:py-28" data-testid="services-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          number="01"
          caption="What We Fix"
          title="Every Major Appliance. Every Major Brand."
          sub="Fully stocked service vans mean most repairs are completed in a single visit — diagnosed, quoted flat, and fixed on the spot."
        />

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {SERVICES.map((service, i) => {
            const Icon = service.icon;
            return (
              <motion.article
                key={service.slug}
                data-testid={`service-card-${service.slug}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: (i % 4) * 0.08, ease: [0.16, 1, 0.3, 1] }}
                className="group relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white transition-[border-color,box-shadow,transform] duration-300 hover:-translate-y-1.5 hover:border-rescue/40 hover:shadow-xl hover:shadow-red-600/5"
              >
                <div className="relative h-36 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.name}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy/60 to-transparent" />
                  <span className="absolute left-4 top-4 font-mono text-xs font-bold text-white/80">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="absolute -bottom-5 left-5 flex h-10 w-10 items-center justify-center rounded-xl bg-rescue text-white shadow-lg shadow-red-600/30">
                    <Icon className="h-5 w-5" />
                  </div>
                </div>
                <div className="p-5 pt-8">
                  <h3 className="font-display text-lg font-bold text-navy">{service.name}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{service.tagline}</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {service.badges.map((badge) => (
                      <span
                        key={badge}
                        className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-wider text-slate-500"
                      >
                        {badge}
                      </span>
                    ))}
                  </div>
                  <Link
                    to={`/services/${service.slug}`}
                    data-testid={`service-link-${service.slug}`}
                    className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-rescue transition-colors hover:text-rescue-dark"
                  >
                    Common problems & pricing
                    <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                  </Link>
                </div>
              </motion.article>
            );
          })}

          <motion.a
            href={PHONE_TEL}
            data-testid="service-card-other"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: 0.24, ease: [0.16, 1, 0.3, 1] }}
            className="group relative flex flex-col justify-between overflow-hidden rounded-2xl bg-navy p-6 text-white transition-transform duration-300 hover:-translate-y-1.5"
          >
            <div
              className="pointer-events-none absolute inset-0 opacity-10"
              style={{
                backgroundImage:
                  "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
                backgroundSize: "32px 32px",
              }}
            />
            <div className="relative">
              <p className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-red-400">
                Not Listed?
              </p>
              <h3 className="mt-3 font-display text-xl font-bold leading-snug">
                If it plugs in, we can probably fix it.
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-300">
                Ranges, range hoods, wine coolers, garburators and more — ask us anything.
              </p>
            </div>
            <div className="relative mt-6 flex items-center gap-2 font-bold text-white">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-rescue transition-transform duration-300 group-hover:rotate-12">
                <Phone className="h-5 w-5" />
              </span>
              <span className="text-sm">Call 905-906-6836</span>
            </div>
          </motion.a>
        </div>
      </div>
    </section>
  );
}
